import { useMutation, useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';
import type {
  CreateFeeProfileRequest,
  FeeProfile,
  ListFeeProfilesResponse,
  UpdateFeeProfileRequest,
} from '@sourcito/schemas';
import { apiFetch } from './api';

// Response schemas omitted on the SPA side: zod's `.default({})` on
// FeeProfileData.categoryOverrides creates an input/output type mismatch
// that doesn't fit ZodType<Output> nicely. Server already validates on
// its end via .parse() in the route handlers — client trusts that.

export const feeProfilesQuery = queryOptions({
  queryKey: ['fee-profiles'] as const,
  queryFn: () => apiFetch<ListFeeProfilesResponse>('/api/fee-profiles'),
  staleTime: 30_000,
});

export function feeProfileQuery(id: string) {
  return queryOptions({
    queryKey: ['fee-profile', id] as const,
    queryFn: () => apiFetch<FeeProfile>(`/api/fee-profiles/${id}`),
    staleTime: 30_000,
  });
}

export function useFeeProfiles() {
  return useQuery(feeProfilesQuery);
}

export function useFeeProfile(id: string) {
  return useQuery(feeProfileQuery(id));
}

export function useCreateFeeProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFeeProfileRequest) =>
      apiFetch<FeeProfile>('/api/fee-profiles', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fee-profiles'] });
    },
  });
}

export function useUpdateFeeProfile(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateFeeProfileRequest) =>
      apiFetch<FeeProfile>(`/api/fee-profiles/${id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fee-profiles'] });
      qc.invalidateQueries({ queryKey: ['fee-profile', id] });
    },
  });
}

export function useDeleteFeeProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<null>(`/api/fee-profiles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fee-profiles'] });
    },
  });
}
